import type { ReactNode } from 'react';
import { InfoHint } from '../../billing/overview/OverviewCard';

// Accounts Overview hub's stat row cards - ac-hub.jsx's HubStat, minus the
// Action Items feature (cancelled) and its "active" ring styling (only
// "Practices" navigates anywhere here, so there's no filter state to mark
// active). Cards 2-4 render as plain divs (no onClick) rather than buttons -
// a deliberate deviation from the mock, so nothing looks clickable that
// isn't.
// 'muted' backs a stat whose source query failed - the placeholder value
// (see HubStatsRow) reads as unavailable rather than a real zero.
export type HubStatTone = 'gray' | 'yellow' | 'muted';

const VALUE_TONE: Record<HubStatTone, string> = {
    gray: 'text-gray-900',
    yellow: 'text-yellow-700',
    muted: 'text-gray-300'
};

interface HubStatCardProps {
    label: string;
    value: number | string;
    sub?: string;
    tone?: HubStatTone;
    // Hover explanation beside the label.
    info?: string;
    onClick?: () => void;
}

const LABEL_CLASS = 'text-[12px] font-semibold text-gray-500 truncate';

function HubStatLabel({ label, info }: Pick<HubStatCardProps, 'label' | 'info'>) {
    if (!info) return <span className={LABEL_CLASS}>{label}</span>;
    return (
        <span className="flex items-center gap-1 min-w-0">
            <span className={LABEL_CLASS}>{label}</span>
            <InfoHint text={info} />
        </span>
    );
}

function HubStatBody({
    label,
    value,
    sub,
    tone = 'gray',
    info
}: Omit<HubStatCardProps, 'onClick'>) {
    return (
        <>
            <HubStatLabel label={label} info={info} />
            <span className={`text-[26px] leading-none font-bold tabular-nums ${VALUE_TONE[tone]}`}>
                {value}
            </span>
            {sub ? <span className="text-[11px] text-gray-400 truncate">{sub}</span> : null}
        </>
    );
}

export function HubStatCard({
    label,
    value,
    sub,
    tone,
    info,
    onClick
}: HubStatCardProps): ReactNode {
    const className =
        'bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] px-4 py-3.5 flex flex-col gap-1.5 min-w-0';

    if (!onClick) {
        return (
            <div className={className}>
                <HubStatBody label={label} value={value} sub={sub} tone={tone} info={info} />
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={onClick}
            className={`${className} text-left hover:border-gray-300 transition-colors`}
        >
            <HubStatBody label={label} value={value} sub={sub} tone={tone} info={info} />
        </button>
    );
}
